import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { updateBooking } from '../middleware/reservation_service/updateRequest';
import { getBookingById } from '../middleware/reservation_service/getRequests';
import { Booking } from '../types/booking';

interface ReservationConfirmationProps {
  reservationId?: string;
}

const ReservationConfirmation: React.FC<ReservationConfirmationProps> = ({
  reservationId: propReservationId,
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reservationId = propReservationId || searchParams.get('id');

  const confirmReservation = async () => {
    if (!reservationId) {
      setError('No reservation ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      // Fetch the booking first, then update it
      const axiosResponse = await getBookingById(reservationId);
      const booking: Booking = axiosResponse.data;

      // Create a new object instead of mutating the original
      const updatedBooking = { ...booking, status: 'CONFIRMED' };
      await updateBooking(reservationId, updatedBooking);

      setSuccess(true);
      setLoading(false);
    } catch (err: any) {
      console.error('Error confirming reservation:', err);
      setError(
        err.response?.data?.message ||
          'Failed to confirm reservation. Please try again.'
      );
      setLoading(false);
      setSuccess(false);
    }
  };

  useEffect(() => {
    confirmReservation();
  }, [reservationId]);

  const handleTryAgain = () => {
    confirmReservation();
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            textAlign: 'center',
            borderRadius: 2,
          }}
        >
          {loading && (
            <Box>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                Confirming Your Reservation...
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please wait while we process your confirmation.
              </Typography>
            </Box>
          )}

          {!loading && success && (
            <Box>
              <CheckCircleIcon
                sx={{ fontSize: 80, color: 'success.main', mb: 2 }}
              />
              <Typography variant="h4" gutterBottom color="success.main">
                Reservation Confirmed Successfully!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your reservation has been confirmed. Thank you for choosing our
                service.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGoHome}
                sx={{ mt: 2 }}
              >
                Go to Home
              </Button>
            </Box>
          )}

          {!loading && error && (
            <Box>
              <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom color="error.main">
                Confirmation Failed
              </Typography>
              <Alert severity="error" sx={{ mt: 2, mb: 3 }}>
                {error}
              </Alert>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleTryAgain}
                >
                  Try Again
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleGoHome}
                >
                  Go to Home
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ReservationConfirmation;
